import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import Underline from "@tiptap/extension-underline"
import Highlight from "@tiptap/extension-highlight"
import { useEffect, useImperativeHandle, forwardRef } from "react"
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Highlighter,
  Link as LinkIcon,
  Unlink,
  List as ListIcon,
  ListOrdered,
  RemoveFormatting,
} from "lucide-react"

export interface CompactRichTextEditorHandle {
  focus: () => void
  clear: () => void
  getHTML: () => string
  isEmpty: () => boolean
}

interface Props {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
  onSubmit?: () => void
  autoFocus?: boolean
}

function ToolbarButton({
  onMouseDown,
  active,
  children,
  title,
}: {
  onMouseDown: (e: React.MouseEvent) => void
  active?: boolean
  children: React.ReactNode
  title: string
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault()
        onMouseDown(e)
      }}
      title={title}
      className={`p-1 rounded transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      }`}
    >
      {children}
    </button>
  )
}

function Separator() {
  return <div className="w-px h-4 bg-border mx-0.5" />
}

const CompactRichTextEditor = forwardRef<CompactRichTextEditorHandle, Props>(function CompactRichTextEditor(
  { value, onChange, placeholder = "Escreva...", minHeight = 80, onSubmit, autoFocus = false },
  ref,
) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      Highlight.configure({ multicolor: false }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          class: "text-primary underline",
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    autofocus: autoFocus,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: {
      attributes: {
        class: "prose dark:prose-invert prose-sm max-w-none px-3 py-2 focus:outline-none",
        style: `min-height:${minHeight}px;`,
      },
      handleKeyDown: (_view, event) => {
        if (onSubmit && event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
          event.preventDefault()
          onSubmit()
          return true
        }
        return false
      },
    },
  })

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
    clear: () => editor?.commands.clearContent(),
    getHTML: () => editor?.getHTML() || "",
    isEmpty: () => !editor || editor.isEmpty,
  }), [editor])

  if (!editor) return null

  function promptForLink() {
    if (!editor) return
    const prev = editor.getAttributes("link").href as string | undefined
    const url = window.prompt("URL do link:", prev || "https://")
    if (url === null) return
    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run()
      return
    }
    let href = url.trim()
    if (!/^https?:\/\//i.test(href) && !/^mailto:/i.test(href)) {
      href = "https://" + href
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run()
  }

  const s = 14

  return (
    <div className="border rounded-md overflow-hidden bg-background focus-within:ring-2 focus-within:ring-ring">
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b bg-muted/30 flex-wrap">
        <ToolbarButton
          onMouseDown={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Negrito (Ctrl+B)"
        >
          <Bold size={s} />
        </ToolbarButton>
        <ToolbarButton
          onMouseDown={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Itálico (Ctrl+I)"
        >
          <Italic size={s} />
        </ToolbarButton>
        <ToolbarButton
          onMouseDown={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("underline")}
          title="Sublinhado (Ctrl+U)"
        >
          <UnderlineIcon size={s} />
        </ToolbarButton>
        <ToolbarButton
          onMouseDown={() => editor.chain().focus().toggleHighlight().run()}
          active={editor.isActive("highlight")}
          title="Grifar"
        >
          <Highlighter size={s} />
        </ToolbarButton>
        <Separator />
        <ToolbarButton
          onMouseDown={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Lista com marcadores"
        >
          <ListIcon size={s} />
        </ToolbarButton>
        <ToolbarButton
          onMouseDown={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Lista numerada"
        >
          <ListOrdered size={s} />
        </ToolbarButton>
        <Separator />
        <ToolbarButton onMouseDown={promptForLink} active={editor.isActive("link")} title="Inserir/editar link">
          <LinkIcon size={s} />
        </ToolbarButton>
        {editor.isActive("link") && (
          <ToolbarButton
            onMouseDown={() => editor.chain().focus().extendMarkRange("link").unsetLink().run()}
            title="Remover link"
          >
            <Unlink size={s} />
          </ToolbarButton>
        )}
        <Separator />
        <ToolbarButton
          onMouseDown={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Remover formatação da seleção"
        >
          <RemoveFormatting size={s} />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  )
})

export default CompactRichTextEditor
